import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Player } from "@lottiefiles/react-lottie-player";
import { useIntl } from "react-intl";

import className from "../style.module.scss";

export function AnimationEntity(props) {
  const intl = useIntl(); // current language = intl.locale
  const player = React.createRef();
  const [triggered, setTriggered] = useState(false);
  const hasReadiedRef = React.useRef(false);

  // Check if playOnce is defined; default to false if undefined
  const playOnce = props.data.playOnce ?? false;

  const baseUrl =
    window.CONTENT_BASE_URL ||
    window.location.origin + window.location.pathname;

  useEffect(() => {
    // Only undo the trigger on mount if the animation hasn't already played.
    // If it has played (state is in playedStates), the trigger is legitimately
    // persisted and dependent hotspots should remain visible.
    if (!props.playedStates.has(props.state)) {
      props.undoTrigger(props.data.customTrigger);
    }
  }, []);

  useEffect(() => {
    if (!props.playedStates.has(props.state)) {
      initiatePlay(); // Only play if state hasn't been played before
    }
  }, [props.state, props.hidden]);

  // Handle pause/resume for looping animations only
  useEffect(() => {
    if (!props.data.loop) {
      return;
    }

    // Skip static animations (start === end)
    if (props.data.frameRange.start === props.data.frameRange.end) {
      return;
    }

    if (props.pauseLooping && !props.hidden) {
      player.current?.pause();
    } else if (!props.pauseLooping && !props.hidden) {
      // Only resume if animation should be playing (within frame range)
      const currentFrame = player.current?.state?.instance?.currentFrame;
      if (
        currentFrame !== undefined &&
        currentFrame >= props.data.frameRange.start &&
        currentFrame <= props.data.frameRange.end
      ) {
        player.current?.play();
      }
    }
  }, [
    props.pauseLooping,
    props.hidden,
    props.data.loop,
    props.data.frameRange,
  ]);

  const initiatePlay = () => {
    if (!props.hidden) {
      player.current?.stop();
      if (props.data.frameRange.start !== props.data.frameRange.end) {
        player.current.setPlayerSpeed(props.data.speed);
        player.current.setSeeker(props.data.frameRange.start, true);

        // Only play if:
        // 1. Animation is NOT looping, OR
        // 2. Animation IS looping but pause is NOT active
        const shouldPlay = !props.data.loop || !props.pauseLooping;

        if (shouldPlay) {
          player.current.play();
        }
      } else {
        player.current.setSeeker(props.data.frameRange.start, false);
      }
    }
  };

  const isLoaded = () => {
    if (!props.playedStates.has(props.state)) {
      initiatePlay();
    }
  };

  const newFrame = () => {
    if (
      Math.ceil(player.current.state.instance.currentFrame) ===
      props.data.frameRange.end
    ) {
      if (props.data.loop) {
        player.current.setSeeker(props.data.frameRange.start, true);
      } else {
        player.current.pause();
      }

      // Mark the state as played at the end frame only if playOnce is true
      if (playOnce && !props.playedStates.has(props.state)) {
        if (props.onStateMarkedPlayed) {
          props.onStateMarkedPlayed(props.state);
        } else {
          props.playedStates.add(props.state);
        }
      }

      if (props.data.customTrigger !== "" && !triggered) {
        setTriggered(true);
        props.fireTrigger(props.data.customTrigger);
      }
    }
  };

  const handleEnd = () => {
    if (props.data.loop) {
      player.current.setSeeker(props.data.frameRange.start, true);
      player.current.play();
    }

    // Mark the state as played on complete only if playOnce is true
    if (playOnce && !props.playedStates.has(props.state)) {
      if (props.onStateMarkedPlayed) {
        props.onStateMarkedPlayed(props.state);
      } else {
        props.playedStates.add(props.state);
      }
    }

    if (props.data.customTrigger !== "" && !triggered) {
      setTriggered(true);
      props.fireTrigger(props.data.customTrigger);
    }
  };

  const [src, setSrc] = useState(
    props.preview
      ? props.previewSrc
      : `${baseUrl}locales/${intl.locale}/animations/${props.src}`
  );

  const loadError = () => {
    props.preview
      ? props.previewSrc
      : setSrc(`${baseUrl}locales/en/animations/${props.src}`);
  };

  useEffect(() => {
    props.preview
      ? props.previewSrc
      : setSrc(`${baseUrl}locales/${intl.locale}/animations/${props.src}`);
  }, [intl.locale]);

  // Prefer caller-supplied parsed JSON when available; fall back to URL.
  const playerSrc = props.animationData ?? src;

  return (
    <div
      className={className.entityContainer}
      style={{
        overflow: !props.allowOverflow ? "hidden" : null,
        display: props.hidden ? "none" : null,
      }}
      aria-hidden={true}
      role="presentation"
    >
      <div className={className.animationEntityContainer} aria-hidden>
        <Player
          key={src} // forces remount when locale URL changes
          ref={player}
          src={playerSrc}
          onEvent={(event) => {
            if (event === "error") loadError();
            if (event === "load") {
              isLoaded();
              if (!hasReadiedRef.current) {
                hasReadiedRef.current = true;
                props.onReady?.();
              }
            }
            if (event === "frame") newFrame();
            if (event === "complete") handleEnd();
          }}
          keepLastFrame
          preserveAspectRatio="xMaxYMid meet"
          style={{
            width: "100%",
          }}
        ></Player>
      </div>
    </div>
  );
}

AnimationEntity.propTypes = {
  data: PropTypes.shape({
    frameRange: PropTypes.shape({
      start: PropTypes.number.isRequired,
      end: PropTypes.number.isRequired,
    }).isRequired,
    speed: PropTypes.number.isRequired,
    loop: PropTypes.bool.isRequired,
    altText: PropTypes.string.isRequired,
    playOnce: PropTypes.bool, // Optional playOnce property
    ariaLive: PropTypes.oneOf(["none", "polite", "assertive"]),
  }).isRequired,
  src: PropTypes.string.isRequired,
  allowOverflow: PropTypes.bool.isRequired,
  state: PropTypes.oneOfType([PropTypes.object, PropTypes.number]).isRequired,
  playedStates: PropTypes.object.isRequired, // Passed from parent
  onStateMarkedPlayed: PropTypes.func, // Optional callback when a state is marked as played
  hidden: PropTypes.bool,
  preview: PropTypes.bool,
  previewSrc: PropTypes.string,
  undoTrigger: PropTypes.func,
  fireTrigger: PropTypes.func,
  pauseLooping: PropTypes.bool, // Control for pausing looping animations
  animationData: PropTypes.object, // Optional pre-fetched Lottie JSON; overrides URL src
  onReady: PropTypes.func, // Optional callback fired once on the lottie 'load' event
};
